import type { PuzzleDefinition } from '@/types';
import { PathwayType } from '@/types';

// ═══════════════════════════════════════════════════════════════════════════
// Chapter 4: "Reflection" — Tier 4
// Introduces Inverting pathways and cyclic topologies.
// Nodes: 6–8, Pathways: 6–9, Budget: minimum + 1–2
// Inverting modifier: -1. Ring 1→-1, Ring 2→-2, Ring 3→-3.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ch4-01: "Mirror"
 * 6 nodes, 6 pathways, 1 strike. Introduces Inverting.
 *
 * Hub A with 5 spokes: B,C,D,E,F.
 * A-B Inverting P5, A-C Clear M3, A-D Clear unison, A-E Inverting m3, A-F Clear P5
 * Plus D-F Clear unison.
 *
 * Strike A: all ring 1.
 *   B: Inverting → delta=1*(-1)=-1. Need |A-B_final| mod12 = 7. A=8.
 *     B_final = B_start + (-1). B_final - 8 ≡ 7 mod 12. B_final=3 or B_final=1.
 *     If B_final=3: B_start=3-(-1)=4. Check: 4+(-1)=3. |8-3|=5. (3-8+12)=7. P5 ✓
 *   C: Clear → delta=1. C_final-8 ≡ 4 mod12. C_final=12 or C_final=4. C_start=11 or 3.
 *     C_start=11: 11+1=12. |8-12|=4. M3 ✓
 *   D: Clear → delta=1. |D_final-8|=0. D_final=8. D_start=7. 7+1=8. ✓
 *   E: Inverting → delta=-1. |E_final-8|mod12=3. E_final=11 or E_final=5.
 *     E_final=11: E_start=11-(-1)=12. 12+(-1)=11. |8-11|=3. m3 ✓
 *   F: Clear → delta=1. |F_final-8|mod12=7. F_final=3 or 1.
 *     F_final=3: F_start=2. 2+1=3. |8-3|=5, (3-8+12)=7. P5 ✓
 *   D-F: |8-3|=5. unison=0 ✗. Hmm.
 *
 * Need D_final=F_final for D-F unison. D_final=8, F_final must=8.
 * But we need A-F P5: |8-F_final|=7. F_final=1 or 3. Not 8.
 * Change D-F to P5: |8-3|=5, (3-8+12)=7. P5 ✓
 *
 * A=8, B=4, C=11, D=7, E=12, F=2. 6 pathways:
 * A-B Inv P5, A-C Clear M3, A-D Clear unison, A-E Inv m3, A-F Clear P5, D-F P5
 *
 * Strike A:
 *   B: Inv → -1. B=4-1=3. |8-3|=5, (3-8+12)=7. P5 ✓
 *   C: Clear → +1. C=11+1=12. |8-12|=4. M3 ✓
 *   D: Clear → +1. D=7+1=8. unison ✓
 *   E: Inv → -1. E=12-1=11. |8-11|=3. m3 ✓
 *   F: Clear → +1. F=2+1=3. |8-3|=5, (3-8+12)=7. P5 ✓
 *   D-F: D=8, F=3. |8-3|=5, (3-8+12)=7. P5 ✓
 * All satisfied! ✓
 *
 * Initially:
 *   A-B: |8-4|=4, P5=7 ✗
 * Not 100% ✓
 */
export const ch4_01: PuzzleDefinition = {
  id: 'ch4-01',
  version: 1,
  tier: 4,
  metadata: {
    title: 'Mirror',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-02-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.5, frequency: 8, locked: false },
      { id: 'B', x: 0.5, y: 0.05, frequency: 4, locked: false },
      { id: 'C', x: 0.9, y: 0.25, frequency: 11, locked: false },
      { id: 'D', x: 0.9, y: 0.75, frequency: 7, locked: false },
      { id: 'E', x: 0.1, y: 0.25, frequency: 12, locked: false },
      { id: 'F', x: 0.1, y: 0.75, frequency: 2, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'A', target: 'E', type: PathwayType.Inverting, harmonic: 'm3', directed: false },
      { id: 'p5', source: 'A', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'D', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch4-02: "Inversion Chain"
 * 6 nodes, 6 pathways, 1 strike.
 *
 * Linear: A--[Inv]--B--[Clear]--C--[Inv]--D
 * Plus branches: B--E, C--F (Clear)
 *
 * A=6. Strike A:
 *   B: ring1, Inv → delta=-1. B_start+(-1)=B_final.
 *   C: ring2, Clear (B-C) → delta=2. C_start+2=C_final.
 *   D: ring3, Inv (C-D) → delta=3*(-1)=-3. D_start-3=D_final.
 *   E: ring2, Clear (B-E) → delta=2. E_start+2=E_final.
 *   F: ring3, Clear (C-F) → delta=3. F_start+3=F_final.
 *
 * Solved targets:
 *   A-B Inv P5: |6-B|=7. B=1 or 11. (1-6+12)=7 ✓ or (11-6)=5. B=1.
 *     B_start=1-(-1)=2.
 *   B-C Clear unison: |1-C|=0. C=1.
 *     C_start=1-2=-1→11.
 *   C-D Inv P5: |1-D|=7. D=8 or 6. (8-1)=7 ✓. D=8.
 *     D_start=8-(-3)=11.
 *   B-E Clear M3: |1-E|=4. E=5 or 9. (5-1)=4 ✓. E=5.
 *     E_start=5-2=3.
 *   C-F Clear m3: |1-F|=3. F=4 or 10. (4-1)=3 ✓. F=4.
 *     F_start=4-3=1.
 *
 * Need 6th pathway. A-E? A=6, E=5. |6-5|=1. Not standard interval.
 * E-F? E=5, F=4. |5-4|=1. No.
 * D-F? D=8, F=4. |8-4|=4. M3 ✓
 *
 * 6 pathways: A-B Inv P5, B-C Clear unison, C-D Inv P5, B-E Clear M3, C-F Clear m3, D-F Clear M3
 *
 * Start: A=6, B=2, C=11, D=11, E=3, F=1
 *
 * Forward:
 *   B: ring1 Inv → -1. B=2-1=1. A-B: (1-6+12)=7 P5 ✓
 *   C: ring2 Clear → +2. C=11+2=1. B-C: |1-1|=0 unison ✓
 *   E: ring2 Clear → +2. E=3+2=5. B-E: |1-5|=4 M3 ✓
 *   D: ring3 Inv → -3. D=11-3=8. C-D: |1-8|=7 P5 ✓
 *   F: ring3 Clear → +3. F=1+3=4. C-F: |1-4|=3 m3 ✓
 *   D-F: |8-4|=4. M3 ✓
 * All satisfied! ✓
 *
 * Initially (A=6,B=2,C=11,D=11,E=3,F=1):
 *   A-B: |6-2|=4 P5=7 ✗
 * Not 100% ✓
 */
export const ch4_02: PuzzleDefinition = {
  id: 'ch4-02',
  version: 1,
  tier: 4,
  metadata: {
    title: 'Inversion Chain',
    category: 'cascade',
    author: 'resonance-team',
    created: '2026-02-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.4, frequency: 6, locked: false },
      { id: 'B', x: 0.3, y: 0.4, frequency: 2, locked: false },
      { id: 'C', x: 0.55, y: 0.4, frequency: 11, locked: false },
      { id: 'D', x: 0.8, y: 0.4, frequency: 11, locked: false },
      { id: 'E', x: 0.3, y: 0.85, frequency: 3, locked: false },
      { id: 'F', x: 0.55, y: 0.85, frequency: 1, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p4', source: 'B', target: 'E', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p5', source: 'C', target: 'F', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p6', source: 'D', target: 'F', type: PathwayType.Clear, harmonic: 'M3', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch4-03: "Counter Flow"
 * 6 nodes, 6 pathways, 2 strikes. Triangle cycle with Inverting.
 *
 * Triangle: A-B-C-A cycle. Plus D off A, E off B, F off C.
 * A-B Inv, B-C Clear, A-C Clear, A-D Clear, B-E Inv, C-F Clear
 *
 * Solved: A=4, B=11, C=4, D=4, E=4, F=4
 *   A-B Inv P5: (11-4)=7 P5 ✓
 *   B-C Clear P5: (11-4)=7 P5 ✓
 *   A-C Clear unison: |4-4|=0 ✓
 *   A-D Clear unison: |4-4|=0 ✓
 *   B-E Inv P5: (4-11+12)=5, (11-4)=7 P5 ✓
 *   C-F Clear unison: |4-4|=0 ✓
 *
 * BFS from D: ring0=D, ring1={A(A-D Clear)}, ring2={B(A-B Inv), C(A-C Clear)}
 * ring3 from B: E(B-E Inv), C(already ring2). From C: F(C-F Clear), B(already ring2).
 *
 * Reverse strike D:
 *   A: ring1 Clear → -1. A=3
 *   B: ring2 Inv → delta=2*(-1)=-2. -(-2)=+2. B=1 (was 11, reverse means B was 11+2=1). Wait.
 *   Reverse: B_before = B_after - delta = 11-(-2) = 13→1
 *   C: ring2 Clear → -2. C=2
 *   E: ring3 Inv → delta=3*(-1)=-3. E_before = 4-(-3) = 7
 *   F: ring3 Clear → -3. F=1
 * Intermediate: A=3, B=1, C=2, D=4, E=7, F=1
 *
 * BFS from A: ring0=A, ring1={B(A-B Inv), C(A-C Clear), D(A-D Clear)}
 * ring2 from B: E(B-E Inv), C(already ring1). From C: F(C-F Clear), B(already ring1).
 * From D: nothing new.
 *
 * Reverse strike A on intermediate:
 *   B: ring1 Inv → delta=-1. B_before = 1-(-1) = 2
 *   C: ring1 Clear → -1. C=1
 *   D: ring1 Clear → -1. D=3
 *   E: ring2 Inv (B-E) → delta=2*(-1)=-2. E_before = 7-(-2) = 9
 *   F: ring2 Clear → -2. F_before = 1-2 = -1 → 11
 * Start: A=3, B=2, C=1, D=3, E=9, F=11
 *
 * Forward:
 * Strike A on (A=3, B=2, C=1, D=3, E=9, F=11):
 *   B: ring1 Inv → -1. B=2-1=1
 *   C: ring1 Clear → +1. C=1+1=2
 *   D: ring1 Clear → +1. D=3+1=4
 *   E: ring2 Inv (B-E) → delta=-2. E=9-2=7
 *   F: ring2 Clear → +2. F=11+2=1
 * State: A=3, B=1, C=2, D=4, E=7, F=1
 *
 * Strike D on (A=3, B=1, C=2, D=4, E=7, F=1):
 *   A: ring1 Clear → +1. A=3+1=4
 *   B: ring2 Inv (A-B) → delta=2*(-1)=-2. B=1-2=-1→11
 *   C: ring2 Clear → +2. C=2+2=4
 *   E: ring3 Inv (B-E) → delta=3*(-1)=-3. E=7-3=4
 *   F: ring3 Clear → +3. F=1+3=4
 * State: A=4, B=11, C=4, D=4, E=4, F=4
 *
 * Check:
 *   A-B Inv P5: (11-4)=7 ✓
 *   B-C Clear P5: (11-4)=7 ✓ (also check (4-11+12)=5, but 7 already matches)
 *   A-C Clear unison: |4-4|=0 ✓
 *   A-D Clear unison: |4-4|=0 ✓
 *   B-E Inv P5: (4-11+12)=5, (11-4)=7 ✓
 *   C-F Clear unison: |4-4|=0 ✓
 * All satisfied! ✓
 *
 * Initially (A=3,B=2,C=1,D=3,E=9,F=11):
 *   A-B: |3-2|=1, (2-3+12)=11. P5=7 ✗
 * Not 100% ✓
 */
export const ch4_03: PuzzleDefinition = {
  id: 'ch4-03',
  version: 1,
  tier: 4,
  metadata: {
    title: 'Counter Flow',
    category: 'flow',
    author: 'resonance-team',
    created: '2026-02-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.15, frequency: 3, locked: false },
      { id: 'B', x: 0.15, y: 0.55, frequency: 2, locked: false },
      { id: 'C', x: 0.85, y: 0.55, frequency: 1, locked: false },
      { id: 'D', x: 0.5, y: 0.4, frequency: 3, locked: false },
      { id: 'E', x: 0.15, y: 0.9, frequency: 9, locked: false },
      { id: 'F', x: 0.85, y: 0.9, frequency: 11, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'B', target: 'E', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'C', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A', 'D'], optimal: true }],
};

/**
 * ch4-04: "Twisted Diamond"
 * 6 nodes, 7 pathways, 1 strike. Diamond with Inverting on diagonals.
 *
 * Layout:      A(3)
 *             / \
 *          B(3)  C(3)
 *             \ /
 *             D(6)
 *          Plus E off B, F off C.
 *
 * A-B Inv P5, A-C Clear M3, B-D Clear M3, C-D Inv P5, B-E Clear unison, C-F Clear unison, E-F Clear P5
 *
 * Strike A: ring0=A
 *   B: ring1 Inv → delta=-1. B=3-1=2
 *   C: ring1 Clear → delta=+1. C=3+1=4
 *   D: ring2, 2 paths: A→B→D (last Clear, delta=2) and A→C→D (last Inv, delta=2*(-1)=-2). Total=0.
 *     D unchanged=6.
 *   E: ring2, path A→B→E (last Clear, delta=2). E=... need to set E_start.
 *   F: ring2, path A→C→F (last Clear, delta=2). F=... need to set F_start.
 *
 * Wait, D gets delta=0 (2+(-2)=0). D stays at 6.
 *
 * Target:
 *   A-B Inv P5: A=3, B=2. (2-3+12)=11, (3-2)=1. Neither is 7. ✗
 *
 * B_final=2 doesn't give P5 with A=3. Need |3-B|=7. B=-4→8 or B=10.
 * With delta=-1: B_start-1=8 → B_start=9. Or B_start=11 → 10.
 *
 * Let me set A=3, B_start=9:
 *   B: 9-1=8. |3-8|=5, (8-3)=5. P5=7. ✗. Hmm, 5≠7.
 *   B_start=4: 4-1=3. |3-3|=0. unison.
 *   B_start needs final B: (B-3) mod12 = 7 or (3-B) mod12 = 7.
 *   B=10: (10-3)=7 ✓. B_start=10-(-1)=11.
 *
 * A=3, B_start=11, C_start needs:
 *   C: delta=+1. A-C M3: |3-C_final|=4. C_final=7 or 11. C_start=6 or 10.
 *
 * D stacking: delta=0, D unchanged.
 *   B-D M3: |10-D|=4. D=6 or D=2. (10-6)=4 ✓.
 *   C-D Inv P5: |C_final-D|=7.
 *     If C_final=7, D=6: |7-6|=1. ✗. D=12: |7-12|=5, (12-7)=5. ✗.
 *     If C_final=7, D needs |7-D|=7. D=12 or D=0→12. (7-12+12)=7 ✓. D=12.
 *     But B-D M3: |10-12|=2. ✗.
 *     If C_final=11, D: |11-D|=7. D=4 or D=6. (11-4)=7 ✓. D=4.
 *     B-D M3: |10-4|=6 ✗. D=6: (11-6)=5, (6-11+12)=7 ✓ P5. But we need M3=4.
 *     B-D needs |10-D|=4. D=6 or D=2.
 *     C-D Inv P5: with D=6 and C_final=11: (11-6)=5, (6-11+12)=7. P5 ✓
 *
 * So C_final=11, D=6. C_start=10.
 *
 * B=10, D=6: B-D M3: |10-6|=4 ✓
 * C=11, D=6: C-D Inv P5: (6-11+12)=7 ✓
 *
 * E: ring2 Clear, delta=2. B-E unison: |10-E_final|=0. E_final=10. E_start=8.
 * F: ring2 Clear, delta=2. C-F unison: |11-F_final|=0. F_final=11. F_start=9.
 * E-F P5: |10-11|=1 ✗. Need |E-F|=7. E=10, F=3: |10-3|=7 ✓.
 * But F must equal C_final=11 for C-F unison. That conflicts.
 *
 * Change C-F to m3: |11-F|=3. F=8 or 2. (11-8)=3 ✓. F_start=6.
 * E-F P5: |10-8|=2 ✗.
 *
 * Change E-F to m3: |10-8|=2 ✗.
 * Change E-F to unison: |10-8|=2 ✗.
 *
 * This is getting complicated. Let me simplify.
 *
 * ch4-04: "Twisted Diamond"
 * 6 nodes, 6 pathways, 1 strike. Simpler design.
 *
 * Hub A with spokes to B,C,D,E. Plus B-E, C-D cross.
 *
 * A=5.
 * A-B Inv unison, A-C Clear P5, A-D Inv unison, A-E Clear M3
 * B-E Clear P5, C-D Clear m3
 *
 * Strike A: all ring 1.
 *   B: Inv → -1. Need |5-B|=0 unison. B=5. B_start=5-(-1)=6.
 *   C: Clear → +1. Need |5-C|=7 P5. C=12 or C=10. (12-5)=7 ✓. C_start=11.
 *   D: Inv → -1. Need |5-D|=0 unison. D=5. D_start=6.
 *   E: Clear → +1. Need |5-E|=4 M3. E=9 or E=1. (9-5)=4 ✓. E_start=8.
 *   B-E P5: |5-9|=4. ✗
 *
 * Hmm. B=5, E=9: |5-9|=4. P5=7. ✗.
 * Change B-E to M3: |5-9|=4. M3=4 ✓
 * C-D m3: |12-5|=7. m3=3. ✗
 * Change C-D to P5: |12-5|=7. P5 ✓
 *
 * Final:
 * A-B Inv unison, A-C Clear P5, A-D Inv unison, A-E Clear M3, B-E Clear M3, C-D Clear P5
 *
 * Solved: A=5, B=5, C=12, D=5, E=9
 * Wait, only 5 nodes referenced. Need node F.
 *
 * Add F off C. C-F Clear unison: F=12. F_start=12-1(ring2 delta)... Wait, F is ring2.
 * BFS from A: ring1={B,C,D,E}. ring2 from B: E already visited. From C: F. From D: nothing. From E: B already.
 * F: ring2, path A→C→F, last pw C-F Clear, delta=2. F_start=12-2=10.
 *
 * Add D-F pathway. D=5, F=12. |5-12|=7. P5 ✓
 *
 * 6 nodes, 7 pathways:
 * A-B Inv unison, A-C Clear P5, A-D Inv unison, A-E Clear M3, B-E Clear M3, C-F Clear unison, D-F Clear P5
 *
 * Start: A=5, B=6, C=11, D=6, E=8, F=10
 *
 * Forward:
 *   B: ring1 Inv → -1. B=6-1=5. A-B: |5-5|=0 unison ✓
 *   C: ring1 Clear → +1. C=11+1=12. A-C: (12-5)=7 P5 ✓
 *   D: ring1 Inv → -1. D=6-1=5. A-D: |5-5|=0 unison ✓
 *   E: ring1 Clear → +1. E=8+1=9. A-E: (9-5)=4 M3 ✓
 *   F: ring2 Clear → +2. F=10+2=12. C-F: |12-12|=0 unison ✓
 *   B-E: |5-9|=4. M3 ✓
 *   D-F: |5-12|=7. P5 ✓
 * All satisfied! ✓
 *
 * Initially (A=5,B=6,C=11,D=6,E=8,F=10):
 *   A-B: |5-6|=1 unison ✗
 * Not 100% ✓
 */
export const ch4_04: PuzzleDefinition = {
  id: 'ch4-04',
  version: 1,
  tier: 4,
  metadata: {
    title: 'Twisted Diamond',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-02-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.5, frequency: 5, locked: false },
      { id: 'B', x: 0.2, y: 0.2, frequency: 6, locked: false },
      { id: 'C', x: 0.8, y: 0.2, frequency: 11, locked: false },
      { id: 'D', x: 0.2, y: 0.8, frequency: 6, locked: false },
      { id: 'E', x: 0.8, y: 0.8, frequency: 8, locked: false },
      { id: 'F', x: 0.95, y: 0.5, frequency: 10, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'A', target: 'E', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p5', source: 'B', target: 'E', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p6', source: 'C', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p7', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'M3', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch4-05: "Phase Shift"
 * 6 nodes, 6 pathways, 2 strikes.
 *
 * Topology: A-B-C triangle, D off A, E off B, F off C.
 * A-B Inv, B-C Clear, A-C Inv, A-D Clear, B-E Clear, C-F Inv
 *
 * Solved: A=6, B=11, C=11, D=6, E=11, F=6
 *   A-B Inv P5: (11-6)=5, (6-11+12)=7. P5 ✓
 *   B-C Clear unison: |11-11|=0 ✓
 *   A-C Inv P5: (11-6)=5, (6-11+12)=7. P5 ✓
 *   A-D Clear unison: |6-6|=0 ✓
 *   B-E Clear unison: |11-11|=0 ✓
 *   C-F Inv P5: (6-11+12)=7. P5 ✓
 *
 * BFS from D: ring0=D, ring1={A(A-D Clear)}
 * ring2 from A: B(A-B Inv), C(A-C Inv)
 * ring3 from B: E(B-E Clear), C(already ring2). From C: F(C-F Inv), B(already ring2).
 *
 * Reverse strike D:
 *   A: ring1 Clear → -1. A=5
 *   B: ring2 Inv → delta=2*(-1)=-2. B=11-(-2)=1
 *   C: ring2 Inv → delta=2*(-1)=-2. C=11-(-2)=1
 *   E: ring3 Clear → -3. E=8
 *   F: ring3 Inv → delta=3*(-1)=-3. F=6-(-3)=9
 * Intermediate: A=5, B=1, C=1, D=6, E=8, F=9
 *
 * BFS from A: ring0=A, ring1={B(Inv), C(Inv), D(Clear)}
 * ring2 from B: E(Clear), C(already ring1). From C: F(Inv), B(already ring1). From D: nothing new.
 *
 * Reverse strike A on intermediate:
 *   B: ring1 Inv → -(-1)=+1. B=1+1=2
 *   C: ring1 Inv → +1. C=1+1=2
 *   D: ring1 Clear → -1. D=5
 *   E: ring2 Clear → -2. E=6
 *   F: ring2 Inv → delta=2*(-1)=-2. F=9-(-2)=11
 * Start: A=5, B=2, C=2, D=5, E=6, F=11
 *
 * Forward:
 * Strike A on (A=5,B=2,C=2,D=5,E=6,F=11):
 *   B: ring1 Inv → -1. B=2-1=1
 *   C: ring1 Inv → -1. C=2-1=1
 *   D: ring1 Clear → +1. D=5+1=6
 *   E: ring2 Clear → +2. E=6+2=8
 *   F: ring2 Inv → delta=-2. F=11-2=9
 * State: A=5, B=1, C=1, D=6, E=8, F=9
 *
 * Strike D on (A=5,B=1,C=1,D=6,E=8,F=9):
 *   A: ring1 Clear → +1. A=5+1=6
 *   B: ring2 Inv → delta=-2. B=1-2=-1→11
 *   C: ring2 Inv → delta=-2. C=1-2=-1→11
 *   E: ring3 Clear → +3. E=8+3=11
 *   F: ring3 Inv → delta=-3. F=9-3=6
 * State: A=6, B=11, C=11, D=6, E=11, F=6
 *
 * Check:
 *   A-B Inv P5: (6-11+12)=7 ✓
 *   B-C Clear unison: |11-11|=0 ✓
 *   A-C Inv P5: (6-11+12)=7 ✓
 *   A-D Clear unison: |6-6|=0 ✓
 *   B-E Clear unison: |11-11|=0 ✓
 *   C-F Inv P5: (6-11+12)=7 ✓
 * All satisfied! ✓
 *
 * Initially (A=5,B=2,C=2,D=5,E=6,F=11):
 *   A-B Inv P5: (5-2)=3 ✗
 * Not 100% ✓
 */
export const ch4_05: PuzzleDefinition = {
  id: 'ch4-05',
  version: 1,
  tier: 4,
  metadata: {
    title: 'Phase Shift',
    category: 'flow',
    author: 'resonance-team',
    created: '2026-02-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.15, frequency: 5, locked: false },
      { id: 'B', x: 0.15, y: 0.5, frequency: 2, locked: false },
      { id: 'C', x: 0.85, y: 0.5, frequency: 2, locked: false },
      { id: 'D', x: 0.5, y: 0.35, frequency: 5, locked: false },
      { id: 'E', x: 0.15, y: 0.85, frequency: 6, locked: false },
      { id: 'F', x: 0.85, y: 0.85, frequency: 11, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'A', target: 'C', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p4', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'B', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'C', target: 'F', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A', 'D'], optimal: true }],
};

/**
 * ch4-06: "Helix"
 * 6 nodes, 7 pathways, 1 strike. All Inv + Clear mix with interesting interference.
 *
 * Hub A with B,C at ring1, D,E,F at ring2.
 * A-B Inv, A-C Clear
 * B-D Clear, B-E Inv
 * C-E Clear, C-F Clear
 * D-F Clear
 *
 * BFS from A: ring0=A, ring1={B,C}
 * ring2 from B: D(B-D Clear), E(B-E Inv). From C: E(C-E Clear), F(C-F Clear).
 * E at ring2 has 2 paths: A→B→E (Inv, delta=2*(-1)=-2) and A→C→E (Clear, delta=2). Total=0. E unchanged.
 *
 * Hmm, E gets 0 total delta. That's a constraint. Let me work with that.
 *
 * A=4, solve for targets:
 *   B: ring1 Inv → delta=-1. B=B_start-1.
 *   C: ring1 Clear → delta=1. C=C_start+1.
 *   D: ring2 Clear → delta=2. D=D_start+2.
 *   E: delta=0. E unchanged.
 *   F: ring2 Clear → delta=2. F=F_start+2.
 *
 * Need harmonics. Let me pick:
 *   A-B Inv P5: |4-B_final|=7. B_final=11 or 9. (11-4)=7 ✓. B_start=12.
 *   A-C Clear M3: |4-C_final|=4. C_final=8 or 12. (8-4)=4 ✓. C_start=7.
 *   B-D Clear m3: |11-D_final|=3. D_final=8 or 2. (11-8)=3 ✓. D_start=6.
 *   B-E Inv m3: |11-E|=3. E=8 or 2. (11-8)=3 ✓. E=8 (unchanged, E_start=8).
 *   C-E Clear unison: |8-8|=0 ✓
 *   C-F Clear P5: |8-F_final|=7. F_final=3 or 1. (8-1)=7 ✓. F_start=11.
 *     Or (8-3)=5, (3-8+12)=7 ✓. F_final=3. F_start=1.
 *   D-F m3: |8-3|=5. ✗. |8-1|=7. P5.
 *   Let F_final=1. D-F: |8-1|=7. P5 ✓. F_start=11.
 *
 * Hmm wait. D_final=8, F_final=1: |8-1|=7 P5 ✓
 *
 * Final pathways:
 * A-B Inv P5, A-C Clear M3, B-D Clear m3, B-E Inv m3, C-E Clear unison, C-F Clear P5, D-F Clear P5
 *
 * Start: A=4, B=12, C=7, D=6, E=8, F=11
 *
 * Forward:
 *   B: ring1 Inv → -1. B=12-1=11. A-B: (11-4)=7 P5 ✓
 *   C: ring1 Clear → +1. C=7+1=8. A-C: (8-4)=4 M3 ✓
 *   D: ring2 Clear → +2. D=6+2=8. B-D: (11-8)=3 m3 ✓
 *   E: ring2 total=0. E=8. B-E: (11-8)=3 m3 ✓. C-E: |8-8|=0 unison ✓
 *   F: ring2 Clear → +2. F=11+2=1. C-F: (8-1)=7 P5 ✓
 *   D-F: (8-1)=7 P5 ✓
 * All 7 satisfied! ✓
 *
 * Initially (A=4,B=12,C=7,D=6,E=8,F=11):
 *   A-B: |4-12|=8 or 4. P5=7 ✗
 * Not 100% ✓
 */
export const ch4_06: PuzzleDefinition = {
  id: 'ch4-06',
  version: 1,
  tier: 4,
  metadata: {
    title: 'Helix',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-02-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.1, frequency: 4, locked: false },
      { id: 'B', x: 0.2, y: 0.35, frequency: 12, locked: false },
      { id: 'C', x: 0.8, y: 0.35, frequency: 7, locked: false },
      { id: 'D', x: 0.1, y: 0.7, frequency: 6, locked: false },
      { id: 'E', x: 0.5, y: 0.65, frequency: 8, locked: false },
      { id: 'F', x: 0.9, y: 0.7, frequency: 11, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'B', target: 'D', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p4', source: 'B', target: 'E', type: PathwayType.Inverting, harmonic: 'm3', directed: false },
      { id: 'p5', source: 'C', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'C', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'D', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch4-07: "Spiral"
 * 7 nodes, 7 pathways, 2 strikes.
 *
 * Topology: A-B-C-D chain, with E off A, F off B, G off C.
 * A-B Inv, B-C Clear, C-D Inv, A-E Clear, B-F Clear, C-G Inv, E-F Inv
 *
 * A=3. Solved state:
 *   Strike A: ring1={B(Inv),E(Clear)}, ring2={C(B-C Clear),F(B-F Clear, also A→E→F via E-F Inv)}, ring3={D(C-D Inv),G(C-G Inv)}
 *
 * F at ring2: paths A→B→F(Clear, delta=2) and A→E→F(Inv, delta=2*(-1)=-2). Total=0. F unchanged.
 *
 * Let me pick solved state and work backward.
 * Solved: A=3, B=10, C=10, D=3, E=3, F=3, G=3
 *   A-B Inv P5: (10-3)=7 ✓
 *   B-C Clear unison: |10-10|=0 ✓
 *   C-D Inv P5: (3-10+12)=5, (10-3)=7 ✓
 *   A-E Clear unison: |3-3|=0 ✓
 *   B-F Clear P5: |10-3|=7 ✓
 *   C-G Inv P5: (3-10+12)=5, (10-3)=7 ✓
 *   E-F Inv unison: |3-3|=0 ✓
 *
 * BFS from A: ring0=A, ring1={B(Inv), E(Clear)}
 * ring2 from B: C(Clear), F(Clear). From E: F(Inv).
 * F at ring2: total delta = 2 + (-2) = 0.
 * ring3 from C: D(Inv), G(Inv). From F: nothing new (E,B already visited).
 *
 * Reverse strike A:
 *   B: ring1 Inv → delta=-1. B_before=10-(-1)=11
 *   E: ring1 Clear → -1. E=2
 *   C: ring2 Clear → -2. C=8
 *   F: ring2 delta=0. F unchanged=3
 *   D: ring3 Inv → delta=-3. D_before=3-(-3)=6
 *   G: ring3 Inv → delta=-3. G_before=3-(-3)=6
 * Intermediate: A=3, B=11, C=8, D=6, E=2, F=3, G=6
 *
 * BFS from C: ring0=C, ring1={B(Clear), D(Inv), G(Inv)}
 * ring2 from B: A(Inv), F(Clear). From D: nothing new (or if D has connections...).
 * D only connects to C. G only connects to C. So ring2={A(B-A Inv),F(B-F Clear)}.
 * ring3 from A: E(A-E Clear). From F: E(E-F Inv). E at ring3: 2 paths.
 * A→E Clear: delta=3. F→E Inv: delta=3*(-1)=-3. But wait, those are both ring3.
 * E has paths: C→B→A→E (Clear, delta=3) and C→B→F→E (Inv, delta=3*(-1)=-3). Total=0.
 *
 * Reverse strike C on intermediate:
 *   B: ring1 Clear → -1. B=10
 *   D: ring1 Inv → delta=-1. D_before=6-(-1)=7
 *   G: ring1 Inv → delta=-1. G_before=6-(-1)=7
 *   A: ring2 Inv → delta=-2. A_before=3-(-2)=5
 *   F: ring2 Clear → -2. F=1
 *   E: ring3 total=0. E unchanged=2
 * Start: A=5, B=10, C=8, D=7, E=2, F=1, G=7
 *
 * Forward:
 * Strike C on (A=5,B=10,C=8,D=7,E=2,F=1,G=7):
 *   B: ring1 Clear → +1. B=11
 *   D: ring1 Inv → -1. D=6
 *   G: ring1 Inv → -1. G=6
 *   A: ring2 Inv → -2. A=3
 *   F: ring2 Clear → +2. F=3
 *   E: ring3 total=0. E=2
 * State: A=3, B=11, C=8, D=6, E=2, F=3, G=6
 *
 * Strike A on (A=3,B=11,C=8,D=6,E=2,F=3,G=6):
 *   B: ring1 Inv → -1. B=10
 *   E: ring1 Clear → +1. E=3
 *   C: ring2 Clear → +2. C=10
 *   F: ring2 total=0. F=3
 *   D: ring3 Inv → -3. D=3
 *   G: ring3 Inv → -3. G=3
 * State: A=3, B=10, C=10, D=3, E=3, F=3, G=3
 *
 * Check:
 *   A-B Inv P5: (10-3)=7 ✓
 *   B-C Clear unison: |10-10|=0 ✓
 *   C-D Inv P5: (10-3)=7 ✓
 *   A-E Clear unison: |3-3|=0 ✓
 *   B-F Clear P5: (10-3)=7 ✓
 *   C-G Inv P5: (10-3)=7 ✓
 *   E-F Inv unison: |3-3|=0 ✓
 * All satisfied! ✓
 *
 * Initially (A=5,B=10,C=8,D=7,E=2,F=1,G=7):
 *   A-B: (10-5)=5 P5=7 ✗
 * Not 100% ✓
 */
export const ch4_07: PuzzleDefinition = {
  id: 'ch4-07',
  version: 1,
  tier: 4,
  metadata: {
    title: 'Spiral',
    category: 'flow',
    author: 'resonance-team',
    created: '2026-02-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.3, frequency: 5, locked: false },
      { id: 'B', x: 0.4, y: 0.3, frequency: 10, locked: false },
      { id: 'C', x: 0.65, y: 0.3, frequency: 8, locked: false },
      { id: 'D', x: 0.9, y: 0.3, frequency: 7, locked: false },
      { id: 'E', x: 0.15, y: 0.75, frequency: 2, locked: false },
      { id: 'F', x: 0.4, y: 0.75, frequency: 1, locked: false },
      { id: 'G', x: 0.65, y: 0.75, frequency: 7, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p4', source: 'A', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'B', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'C', target: 'G', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'E', target: 'F', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 4,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['C', 'A'], optimal: true }],
};

/**
 * ch4-08: "Reverb"
 * 6 nodes, 6 pathways, 2 strikes. Inv + Amp combination.
 *
 * Topology: A(hub)-B, A-C, A-D, B-E, C-F, E-F
 *
 * A-B Inv, A-C Amp, A-D Clear, B-E Clear, C-F Clear, E-F Inv
 *
 * A=2. Solved: A=2, B=9, C=6, D=2, E=9, F=2
 *   A-B Inv P5: (9-2)=7 ✓
 *   A-C Amp M3: (6-2)=4 ✓
 *   A-D Clear unison: |2-2|=0 ✓
 *   B-E Clear unison: |9-9|=0 ✓
 *   C-F Clear M3: |6-2|=4 ✓
 *   E-F Inv P5: (2-9+12)=5, (9-2)=7 ✓
 *
 * BFS from A: ring0=A, ring1={B(Inv),C(Amp),D(Clear)}
 * ring2 from B: E(Clear). From C: F(Clear). From D: nothing.
 * ring3 from E: F(Inv). F already ring2.
 *
 * Reverse strike A:
 *   B: ring1 Inv → delta=-1. B_before=9-(-1)=10
 *   C: ring1 Amp → delta=2. C_before=6-2=4
 *   D: ring1 Clear → -1. D=1
 *   E: ring2 Clear → -2. E=7
 *   F: ring2 Clear → -2. F=12
 * Intermediate: A=2, B=10, C=4, D=1, E=7, F=12
 *
 * BFS from D: ring0=D, ring1={A(Clear)}
 * ring2 from A: B(Inv), C(Amp). ring3 from B: E(Clear). From C: F(Clear).
 *
 * Reverse strike D on intermediate:
 *   A: ring1 Clear → -1. A=1
 *   B: ring2 Inv → delta=-2. B_before=10-(-2)=12
 *   C: ring2 Amp → delta=4. C_before=4-4=0→12
 *   E: ring3 Clear → -3. E=4
 *   F: ring3 Clear → -3. F=9
 * Start: A=1, B=12, C=12, D=1, E=4, F=9
 *
 * Forward:
 * Strike D on (A=1,B=12,C=12,D=1,E=4,F=9):
 *   A: ring1 Clear → +1. A=2
 *   B: ring2 Inv → -2. B=10
 *   C: ring2 Amp → +4. C=12+4=4
 *   E: ring3 Clear → +3. E=7
 *   F: ring3 Clear → +3. F=12
 * State: A=2, B=10, C=4, D=1, E=7, F=12
 *
 * Strike A on (A=2,B=10,C=4,D=1,E=7,F=12):
 *   B: ring1 Inv → -1. B=9
 *   C: ring1 Amp → +2. C=6
 *   D: ring1 Clear → +1. D=2
 *   E: ring2 Clear → +2. E=9
 *   F: ring2 Clear → +2. F=2
 * State: A=2, B=9, C=6, D=2, E=9, F=2
 *
 * Check:
 *   A-B Inv P5: (9-2)=7 ✓
 *   A-C Amp M3: (6-2)=4 ✓
 *   A-D Clear unison: |2-2|=0 ✓
 *   B-E Clear unison: |9-9|=0 ✓
 *   C-F Clear M3: |6-2|=4 ✓
 *   E-F Inv P5: (9-2)=7 ✓
 * All satisfied! ✓
 *
 * Initially (A=1,B=12,C=12,D=1,E=4,F=9):
 *   A-B: (12-1)=11. P5=7 ✗
 * Not 100% ✓
 */
export const ch4_08: PuzzleDefinition = {
  id: 'ch4-08',
  version: 1,
  tier: 4,
  metadata: {
    title: 'Reverb',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-02-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.3, frequency: 1, locked: false },
      { id: 'B', x: 0.2, y: 0.1, frequency: 12, locked: false },
      { id: 'C', x: 0.8, y: 0.1, frequency: 12, locked: false },
      { id: 'D', x: 0.5, y: 0.55, frequency: 1, locked: false },
      { id: 'E', x: 0.2, y: 0.8, frequency: 4, locked: false },
      { id: 'F', x: 0.8, y: 0.8, frequency: 9, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'B', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'C', target: 'F', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['D', 'A'], optimal: true }],
};

/**
 * ch4-09: "Interference"
 * 7 nodes, 8 pathways, 2 strikes. Cyclic with Inv cancellation.
 *
 * Topology: Square A-B-C-D with E hub, F off B, G off D.
 * A-B Clear, B-C Inv, C-D Clear, D-A Inv
 * A-E Clear, B-F Inv, C-E Clear, D-G Clear
 *
 * BFS from E: ring0=E, ring1={A(Clear), C(Clear)}
 * ring2 from A: B(Clear), D(Inv). From C: B(Inv), D(Clear).
 * B at ring2: 2 paths. A→B (Clear, delta=2) and C→B (Inv, delta=2*(-1)=-2). Total=0. B unchanged.
 * D at ring2: 2 paths. A→D (Inv, delta=-2) and C→D (Clear, delta=2). Total=0. D unchanged.
 * ring3 from B: F(Inv). From D: G(Clear).
 *
 * Nice! B and D stay fixed. E affects A,C at ring1, and F,G at ring3.
 *
 * Solved: E=5, A=5, C=5, B=12, D=12, F=5, G=5
 *   A-B Clear P5: |5-12|=7 ✓
 *   B-C Inv P5: (5-12+12)=5, (12-5)=7 ✓
 *   C-D Clear P5: |5-12|=7 ✓
 *   D-A Inv P5: (5-12+12)=5, (12-5)=7 ✓
 *   A-E Clear unison: |5-5|=0 ✓
 *   B-F Inv P5: (5-12+12)=5, (12-5)=7 ✓
 *   C-E Clear unison: |5-5|=0 ✓
 *   D-G Clear P5: (12-5)=7 ✓
 *
 * Reverse strike E:
 *   A: ring1 Clear → -1. A=4
 *   C: ring1 Clear → -1. C=4
 *   B: ring2 total=0. B=12
 *   D: ring2 total=0. D=12
 *   F: ring3 Inv → delta=-3. F_before=5-(-3)=8
 *   G: ring3 Clear → -3. G=2
 * Intermediate: E=5, A=4, C=4, B=12, D=12, F=8, G=2
 *
 * BFS from A: ring0=A, ring1={B(Clear), D(D-A Inv), E(A-E Clear)}
 * ring2 from B: C(B-C Inv), F(B-F Inv). From D: C(C-D Clear), G(D-G Clear). From E: C(C-E Clear).
 * C at ring2: 3 paths: A→B→C(Inv,delta=-2), A→D→C(Clear,delta=2), A→E→C(Clear,delta=2). Total=2.
 * F at ring2: A→B→F(Inv,delta=-2).
 * G at ring2: A→D→G(Clear,delta=2).
 * ring3 from C: D(already ring1). E(already ring1). etc.
 *
 * Reverse strike A on intermediate:
 *   B: ring1 Clear → -1. B=11
 *   D: ring1 Inv → delta=-1. D_before=12-(-1)=1
 *   E: ring1 Clear → -1. E=4
 *   C: ring2 total=2. C_before=4-2=2
 *   F: ring2 Inv → delta=-2. F_before=8-(-2)=10
 *   G: ring2 Clear → -2. G=12
 * Start: A=4, B=11, C=2, D=1, E=4, F=10, G=12
 *
 * Forward:
 * Strike A on (A=4,B=11,C=2,D=1,E=4,F=10,G=12):
 *   B: ring1 Clear → +1. B=12
 *   D: ring1 Inv → -1. D=12 (1-1=0→12)
 *   E: ring1 Clear → +1. E=5
 *   C: ring2 total=2. C=2+2=4
 *   F: ring2 Inv → -2. F=8
 *   G: ring2 Clear → +2. G=2
 * State: A=4, B=12, C=4, D=12, E=5, F=8, G=2
 *
 * Strike E on (A=4,B=12,C=4,D=12,E=5,F=8,G=2):
 *   A: ring1 Clear → +1. A=5
 *   C: ring1 Clear → +1. C=5
 *   B: ring2 total=0. B=12
 *   D: ring2 total=0. D=12
 *   F: ring3 Inv → -3. F=5
 *   G: ring3 Clear → +3. G=5
 * State: A=5, B=12, C=5, D=12, E=5, F=5, G=5
 *
 * Check:
 *   A-B Clear P5: |5-12|=7 ✓
 *   B-C Inv P5: (12-5)=7 ✓
 *   C-D Clear P5: |5-12|=7 ✓
 *   D-A Inv P5: (12-5)=7 ✓
 *   A-E Clear unison: |5-5|=0 ✓
 *   B-F Inv P5: (12-5)=7 ✓
 *   C-E Clear unison: |5-5|=0 ✓
 *   D-G Clear P5: (12-5)=7 ✓
 * All 8 satisfied! ✓
 *
 * Initially (A=4,B=11,C=2,D=1,E=4,F=10,G=12):
 *   A-B: |4-11|=7. P5 ✓ (one pre-satisfied)
 *   B-C: |11-2|=9. P5 ✗
 * Not 100% ✓
 */
/**
 * ch4-09 redesigned: "Interference"
 * 7 nodes, 8 pathways, 2 strikes. Inverting cancellation at ring2.
 *
 * Topology: A-B-C-D chain. B-E, C-F branches. E-F link. A-G link.
 * B-C and C-F are Inverting; rest are Clear except D-G Inverting.
 *
 * Key mechanic: F has 2 paths from B: B->C->F (Inv) and B->E->F (Clear).
 * At ring2 these cancel out (delta=-2 and delta=+2 = 0), leaving F unchanged.
 *
 * Solved: A=3, B=3, C=10, D=10, E=3, F=10, G=10
 *   A-B Clear unison: ✓
 *   B-C Inv P5: (10-3)=7 ✓
 *   C-D Clear unison: ✓
 *   B-E Clear unison: ✓
 *   C-F Inv unison: ✓
 *   E-F Clear P5: (10-3)=7 ✓
 *   D-G Inv unison: ✓
 *   A-G Clear P5: (10-3)=7 ✓
 *
 * Solution: Strike G, then Strike B.
 * Start: A=1, B=1, C=9, D=9, E=11, F=1, G=8
 *
 * Strike G: ring1={A(+1 Clear),D(-1 Inv)}, ring2={B(+2),C(+2)}, ring3={E(+3),F(-3 Inv)}
 *   A=2, D=8, B=3, C=11, E=2, F=10
 * Strike B: ring1={A(+1),C(-1 Inv),E(+1)}, ring2={G(+2),D(+2)}, F: 2 paths cancel=0
 *   A=3, C=10, E=3, G=10, D=10, F=10
 */
export const ch4_09: PuzzleDefinition = {
  id: 'ch4-09',
  version: 1,
  tier: 4,
  metadata: {
    title: 'Interference',
    category: 'constraint',
    author: 'resonance-team',
    created: '2026-02-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.2, frequency: 1, locked: false },
      { id: 'B', x: 0.45, y: 0.2, frequency: 1, locked: false },
      { id: 'C', x: 0.7, y: 0.2, frequency: 9, locked: false },
      { id: 'D', x: 0.9, y: 0.2, frequency: 9, locked: false },
      { id: 'E', x: 0.45, y: 0.65, frequency: 11, locked: false },
      { id: 'F', x: 0.7, y: 0.65, frequency: 1, locked: false },
      { id: 'G', x: 0.05, y: 0.5, frequency: 8, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'B', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'C', target: 'F', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'D', target: 'G', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p8', source: 'A', target: 'G', type: PathwayType.Clear, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 4,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['G', 'B'], optimal: true }],
};

/**
 * ch4-10: "Resonance" — Chapter capstone
 * 8 nodes, 9 pathways, 2 strikes. Complex cyclic with Inv + Clear + Amp.
 *
 * Topology: Outer ring A-B-C-D square, inner ring E-F connected to outer.
 * A-B Clear, B-C Inv, C-D Clear, D-A Inv
 * A-E Clear, C-E Clear, B-F Amp, D-F Amp
 * E-F Inv
 *
 * BFS from E: ring0=E, ring1={A(Clear), C(Clear), F(Inv)}
 * ring2 from A: B(Clear), D(Inv). From C: B(Inv), D(Clear). From F: B(Amp), D(Amp).
 * B at ring2: 3 paths. A→B (Clear,2), C→B (Inv,-2), F→B (Amp,4). Total=4.
 * D at ring2: 3 paths. A→D (Inv,-2), C→D (Clear,2), F→D (Amp,4). Total=4.
 *
 * Hmm, large deltas. Let me verify.
 * Ring2: delta base = ring * modifier = 2 * modifier.
 * B: Clear=2, Inv=2*(-1)=-2, Amp=2*2=4. Sum=4.
 * D: Inv=2*(-1)=-2, Clear=2, Amp=2*2=4. Sum=4.
 *
 * Solved: E=3, A=3, C=3, F=10, B=7, D=7
 * After strike E: A=3+1=4. Wait no, I should solve.
 *
 * Let me pick solved state directly:
 * All P5 on outer ring, unison on inner connections.
 * Solved: A=3, B=10, C=3, D=10, E=3, F=10, plus G, H.
 *
 * Actually let me use 8 nodes. Add G off E and H off F.
 * A-B Clear P5, B-C Inv P5, C-D Clear P5, D-A Inv P5
 * A-E Clear unison, C-E Clear unison, B-F Amp unison, D-F Amp unison
 * E-G Clear P5
 * Wait need 9 pathways. Let me add E-F Inv, and connect G,H.
 *
 * Let me simplify. 8 nodes, 9 pathways.
 *
 * Outer square: A-B-C-D.
 * Cross center: E connected to A and C. F connected to B and D.
 * G off E, H off F.
 * E-F Inv.
 *
 * A-B Clear, B-C Inv, C-D Clear, D-A Inv
 * A-E Clear, C-E Clear, B-F Clear, D-F Clear, E-F Inv
 *
 * That's only 9 pathways but 8 nodes including G,H. Let me drop G,H and use 6 nodes.
 * Actually the prompt says 6-8 nodes. Let me use 8 with a different topology.
 *
 * Let me just do 7 nodes, 8 pathways:
 *
 * A-B Clear P5, B-C Inv P5, C-D Clear P5, D-A Inv P5, A-E Clear unison,
 * C-E Clear unison, B-F Inv P5, D-G Clear P5, E-F Clear M3
 *
 * Actually this is getting complex. Let me use a verified simple approach.
 *
 * ch4-10: "Resonance"
 * 7 nodes, 8 pathways, 2 strikes.
 *
 * Topology: Hub E connected to A,B,C,D (square outer). Plus F off A, G off C.
 * E-A Clear, E-B Inv, E-C Clear, E-D Inv
 * A-B Clear, C-D Clear, A-F Inv, C-G Inv
 *
 * Solved: E=6, A=6, B=11, C=6, D=11, F=11, G=11
 *   E-A Clear unison: |6-6|=0 ✓
 *   E-B Inv P5: (11-6)=5, (6-11+12)=7 ✓
 *   E-C Clear unison: |6-6|=0 ✓
 *   E-D Inv P5: (11-6)=5, (6-11+12)=7 ✓
 *   A-B Clear P5: |6-11|=5, (11-6)=7 ✓... wait (6-11 mod12)=(6-11+12)=7. P5 ✓
 *   C-D Clear P5: same as above. P5 ✓
 *   A-F Inv P5: (11-6)=5, (6-11+12)=7 ✓
 *   C-G Inv P5: same. ✓
 *
 * BFS from E: ring0=E, ring1={A(Clear), B(Inv), C(Clear), D(Inv)}
 * ring2 from A: B(Clear), F(Inv). B already ring1, skip. F at ring2.
 * ring2 from B: A(Clear), already visited.
 * ring2 from C: D(Clear), G(Inv). D already ring1, skip. G at ring2.
 * ring2 from D: C(Clear), already visited.
 *
 * Reverse strike E:
 *   A: ring1 Clear → -1. A=5
 *   B: ring1 Inv → delta=-1. B_before=11-(-1)=12
 *   C: ring1 Clear → -1. C=5
 *   D: ring1 Inv → delta=-1. D_before=11-(-1)=12
 *   F: ring2 Inv → delta=-2. F_before=11-(-2)=1
 *   G: ring2 Inv → delta=-2. G_before=11-(-2)=1
 * Intermediate: E=6, A=5, B=12, C=5, D=12, F=1, G=1
 *
 * BFS from A: ring0=A, ring1={E(Clear), B(A-B Clear), F(A-F Inv)}
 * ring2 from E: C(Clear), B(Inv already ring1), D(Inv). From B: E already ring1.
 * C at ring2 from E. D at ring2 from E.
 * ring3 from C: D(C-D Clear already ring2), G(C-G Inv).
 * ring3 from D: C already ring2.
 *
 * Reverse strike A on intermediate:
 *   E: ring1 Clear → -1. E=5
 *   B: ring1 Clear → -1. B=11
 *   F: ring1 Inv → delta=-1. F_before=1-(-1)=2
 *   C: ring2 Clear (E-C) → -2. C=3
 *   D: ring2 Inv (E-D) → delta=-2. D_before=12-(-2)=2
 *   G: ring3 Inv (C-G) → delta=-3. G_before=1-(-3)=4
 * Start: A=5, E=5, B=11, C=3, D=2, F=2, G=4
 *
 * Forward:
 * Strike A on (A=5,E=5,B=11,C=3,D=2,F=2,G=4):
 *   E: ring1 Clear → +1. E=6
 *   B: ring1 Clear → +1. B=12
 *   F: ring1 Inv → -1. F=1
 *   C: ring2 Clear → +2. C=5
 *   D: ring2 Inv → -2. D=12
 *   G: ring3 Inv → -3. G=1
 * State: A=5, E=6, B=12, C=5, D=12, F=1, G=1
 *
 * Strike E on (A=5,E=6,B=12,C=5,D=12,F=1,G=1):
 *   A: ring1 Clear → +1. A=6
 *   B: ring1 Inv → -1. B=11
 *   C: ring1 Clear → +1. C=6
 *   D: ring1 Inv → -1. D=11
 *   F: ring2 Inv → -2. F=11
 *   G: ring2 Inv → -2. G=11
 * State: A=6, B=11, C=6, D=11, E=6, F=11, G=11
 *
 * Check:
 *   E-A unison: |6-6|=0 ✓
 *   E-B Inv P5: (6-11+12)=7 ✓
 *   E-C unison: |6-6|=0 ✓
 *   E-D Inv P5: (6-11+12)=7 ✓
 *   A-B P5: (11-6)=5, (6-11+12)=7 ✓
 *   C-D P5: (11-6)=5, (6-11+12)=7 ✓
 *   A-F Inv P5: (6-11+12)=7... wait F=11, A=6. (11-6)=5, (6-11+12)=7 ✓
 *   C-G Inv P5: same logic. ✓
 * All 8 satisfied! ✓
 *
 * Initially (A=5,E=5,B=11,C=3,D=2,F=2,G=4):
 *   E-A: |5-5|=0 unison ✓
 *   E-B Inv P5: (5-11+12)=6. P5=7 ✗
 * Not 100% ✓
 */
export const ch4_10: PuzzleDefinition = {
  id: 'ch4-10',
  version: 1,
  tier: 4,
  metadata: {
    title: 'Resonance',
    category: 'composition',
    author: 'resonance-team',
    created: '2026-02-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.3, y: 0.15, frequency: 5, locked: false },
      { id: 'B', x: 0.7, y: 0.15, frequency: 11, locked: false },
      { id: 'C', x: 0.7, y: 0.55, frequency: 3, locked: false },
      { id: 'D', x: 0.3, y: 0.55, frequency: 2, locked: false },
      { id: 'E', x: 0.5, y: 0.35, frequency: 5, locked: false },
      { id: 'F', x: 0.15, y: 0.35, frequency: 2, locked: false },
      { id: 'G', x: 0.85, y: 0.8, frequency: 4, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'E', target: 'A', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'E', target: 'B', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'E', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'E', target: 'D', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p5', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'A', target: 'F', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p8', source: 'C', target: 'G', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 4,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

export const chapter4Puzzles: PuzzleDefinition[] = [
  ch4_01, ch4_02, ch4_03, ch4_04, ch4_05,
  ch4_06, ch4_07, ch4_08, ch4_09, ch4_10,
];
